/**
 * Helpers for company working days and non-working dates.
 * Use when building offer timelines, suggesting dates, or filtering calendar.
 */

import { toLocalDateString } from './dateUtils';

export type CompanyNonWorkingDate = { date: string; label?: string | null };

/** Default Mon–Fri when company has not configured working_days (0=Sun … 6=Sat). */
export const DEFAULT_COMPANY_WORKING_DAYS: number[] = [1, 2, 3, 4, 5];

export type ResolvedScheduleCalendar = {
	workingDays: number[];
	nonWorkingDates: CompanyNonWorkingDate[];
};

/** Always resolve a calendar for scheduling: company settings or Mon–Fri + holidays. */
export function resolveScheduleCalendar(
	workingDays?: number[] | null,
	nonWorkingDates?: CompanyNonWorkingDate[] | null
): ResolvedScheduleCalendar {
	return {
		workingDays:
			Array.isArray(workingDays) && workingDays.length > 0 ? workingDays : DEFAULT_COMPANY_WORKING_DAYS,
		nonWorkingDates: Array.isArray(nonWorkingDates) ? nonWorkingDates : []
	};
}

/**
 * Returns true if the given date is a working day for the company:
 * - If workingDays is null or empty, every day is considered working (backward compat).
 * - Otherwise the date's weekday (0=Sun..6=Sat) must be in workingDays.
 * - The date must not be in nonWorkingDates.
 * Uses local date for comparison so calendar cells match stored dates.
 */
export function isCompanyWorkingDay(
	date: Date | string,
	workingDays: number[] | null | undefined,
	nonWorkingDates: CompanyNonWorkingDate[] | undefined
): boolean {
	const d = typeof date === 'string' ? new Date(date + 'T12:00:00') : date;
	const dateOnly = typeof date === 'string' ? date : toLocalDateString(d);
	const weekday = d.getDay();

	if (Array.isArray(nonWorkingDates) && nonWorkingDates.some((n) => n.date === dateOnly)) {
		return false;
	}
	if (!Array.isArray(workingDays) || workingDays.length === 0) {
		return true;
	}
	return workingDays.includes(weekday);
}

/**
 * Next working day on or after the given date.
 */
export function nextCompanyWorkingDay(
	from: Date | string,
	workingDays: number[] | null | undefined,
	nonWorkingDates: CompanyNonWorkingDate[] | undefined
): Date {
	const d = typeof from === 'string' ? new Date(from + 'T12:00:00') : new Date(from.getTime());
	for (let i = 0; i < 366; i++) {
		if (isCompanyWorkingDay(d, workingDays, nonWorkingDates)) return d;
		d.setDate(d.getDate() + 1);
	}
	return d;
}

/**
 * Add N company working days to a date (skips non-working days).
 */
export function addCompanyWorkdays(
	from: Date | string,
	workdays: number,
	workingDays: number[] | null | undefined,
	nonWorkingDates: CompanyNonWorkingDate[] | undefined
): Date {
	let cur = typeof from === 'string' ? new Date(from + 'T12:00:00') : new Date(from.getTime());
	let added = 0;
	while (added < workdays) {
		if (isCompanyWorkingDay(cur, workingDays, nonWorkingDates)) added++;
		if (added < workdays) cur.setDate(cur.getDate() + 1);
	}
	return cur;
}

/**
 * Count company working days between start and end (inclusive).
 */
export function countCompanyWorkdaysBetween(
	start: Date | string,
	end: Date | string,
	workingDays: number[] | null | undefined,
	nonWorkingDates: CompanyNonWorkingDate[] | undefined
): number {
	const startD = typeof start === 'string' ? new Date(start + 'T12:00:00') : new Date(start.getTime());
	const endD = typeof end === 'string' ? new Date(end + 'T12:00:00') : new Date(end.getTime());
	startD.setHours(0, 0, 0, 0);
	endD.setHours(0, 0, 0, 0);
	if (endD < startD) return 0;
	let count = 0;
	const cur = new Date(startD);
	while (cur <= endD) {
		if (isCompanyWorkingDay(cur, workingDays, nonWorkingDates)) count++;
		cur.setDate(cur.getDate() + 1);
	}
	return count;
}

/**
 * Get the calendar date for the Nth company workday (1-based). Day 1 = first company workday on or after from.
 */
export function getDateForCompanyWorkdayIndex(
	from: Date | string,
	workdayIndex: number,
	workingDays: number[] | null | undefined,
	nonWorkingDates: CompanyNonWorkingDate[] | undefined
): Date {
	const first = nextCompanyWorkingDay(from, workingDays, nonWorkingDates);
	if (workdayIndex <= 1) return first;
	return addCompanyWorkdays(first, workdayIndex - 1, workingDays, nonWorkingDates);
}
